import { observer } from "mobx-react";
import type React from "react";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DEFAULT_ROWS_PER_PAGE } from "constants/misc-constants";
import { AdminModals } from "constants/modals-constants";
import { ADMIN_ENVIRONMENT_ROUTES } from "constants/routes";

import type { SchoolResponse } from "models/admin/Admin";
import { SchoolType } from "models/auth/Login";

import { EncircledIcon } from "components/EncircledIcon";
import type { TableRow } from "components/SortableTable/SortableTable.model";
import { StatusTag } from "components/StatusTag/StatusTag";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";

import { useAdmin } from "hooks/useAdmin";
import { useModal } from "hooks/useModal";

import { CssFlex, CssIcon } from "styles/helpers/layout";
import { ConfirmSignpostSchoolDeletionModal } from "../SignpostSchoolDeletionModal/SignpostSchoolDeletionModal";
import {
  StyledActionButton,
  StyledHeader,
  StyledSortableTable,
  StyledTableContainer,
  StyledTableHeader,
} from "./SchoolsTable.styled";

interface SchoolTableProps {
  page: number;
  variant: SchoolType;
  onSchoolDeleted: () => void;
}

export const SchoolsTable: React.FC<SchoolTableProps> = observer(
  ({ page, variant, onSchoolDeleted }) => {
    const navigate = useNavigate();
    const { t } = useTranslation("admin-environment");
    const { openModal } = useModal();
    const { schoolList: schools = [], archiveSchool, deleteSchool } = useAdmin();

    const startIndex = (page - 1) * DEFAULT_ROWS_PER_PAGE;
    const endIndex = startIndex + DEFAULT_ROWS_PER_PAGE;
    const paginatedItems = schools?.slice(startIndex, endIndex);

    // Note: there is no pagination on this endpoint, so we do client side pagination.
    // The pagination component assumes server-side pagination, so it's a bit awkward to
    // have to grab it from the URL and pass it in as a prop, then slice the data.

    const onDeleteButtonClick = (school: SchoolResponse) => {
      // Show warning modal if school has Signpost enabled
      if (school.signpostLoginEnabled) {
        openModal(AdminModals.ConfirmSignpostSchoolDeletion, {
          schoolId: school.id,
          schoolName: school.title,
        });
        return;
      }

      // Trigger immediate delete if Signpost is not enabled
      deleteSchool(school.id).then(onSchoolDeleted);
    };

    const rowData = useMemo<TableRow[]>(
      () =>
        paginatedItems.map((school) => {
          const linkedUserCount = school.educations.reduce(
            (total, currentEducation) => total + currentEducation.linkedUserCount,
            0,
          );

          return {
            id: school.id,
            dataCy: "school-row",
            cells: [
              {
                id: `school-${school.id}-name`,
                data: school.title,
                dataCy: "school-name",
                content: (
                  <CssFlex flexDirection="row" gap={1}>
                    {school.type === SchoolType.SCHOOL ? (
                      <EncircledIcon
                        iconName="fas fa-graduation-cap"
                        size="medium"
                        variant="primary"
                      />
                    ) : (
                      <EncircledIcon
                        iconName="fas fa-briefcase"
                        size="medium"
                        variant="secondary"
                      />
                    )}
                    {`${school.title}`}
                  </CssFlex>
                ),
              },
              {
                id: `school-${school.id}-domain`,
                data: school.domain,
                content: (
                  <CssFlex flexDirection="row" gap={1} alignItems="center">
                    <span data-cy="school-domain">{school.domain}</span>
                    {school.signpostLoginEnabled && (
                      <StatusTag
                        dataCy="signpost-enabled-tag"
                        icon="far fa-sign-in"
                        label={t("schoolList.statusTags.signpostEnabled")}
                        variant="info"
                      />
                    )}
                  </CssFlex>
                ),
              },
              {
                id: `school-${school.id}-linked-user-count`,
                dataCy: "school-linked-user-count",
                data: linkedUserCount,
                content: `${linkedUserCount}`,
              },
              {
                id: `school-${school.id}-actions`,
                content: (
                  <CssFlex flexDirection="row" gap={1.5}>
                    <StyledActionButton
                      aria-label={t("schoolList.itemActions.editButton.label")}
                      data-cy="school-details-button"
                      onClick={() =>
                        navigate(
                          buildUrlWithPathParams(
                            variant === SchoolType.SCHOOL
                              ? ADMIN_ENVIRONMENT_ROUTES.SCHOOL_DETAILS
                              : ADMIN_ENVIRONMENT_ROUTES.COMPANY_DETAILS,
                            {
                              schoolId: school.id,
                            },
                          ),
                        )
                      }
                    >
                      <CssIcon iconName="fas fa-pencil" />
                    </StyledActionButton>
                    <StyledActionButton
                      aria-label={t("schoolList.itemActions.archiveButton.label")}
                      data-cy="school-archive-button"
                      onClick={() => archiveSchool(school.id)}
                    >
                      <CssIcon iconName="fas fa-archive" />
                    </StyledActionButton>
                    <StyledActionButton
                      aria-label={t("schoolList.itemActions.deleteButton.label")}
                      data-cy="school-delete-button"
                      disabled={linkedUserCount > 0}
                      onClick={() => onDeleteButtonClick(school)}
                    >
                      <CssIcon iconName="fas fa-trash" />
                    </StyledActionButton>
                  </CssFlex>
                ),
              },
            ],
          };
        }),
      [paginatedItems],
    );

    const headerCells = [
      {
        id: "name",
        title: t("schoolTableColumns.name", "Name"),
        size: "1.5fr",
      },
      {
        id: "domain",
        title: t("schoolTableColumns.domain", "Domain"),
        size: "1.5fr",
      },
      {
        id: "linked-user-count",
        title: t("schoolTableColumns.userCount", "Users"),
        size: "minmax(10rem, 0.5fr)",
      },
      {
        id: "actions",
        title: " ",
        size: "auto",
      },
    ];

    return (
      <StyledTableContainer>
        <StyledTableHeader>
          <StyledHeader>
            <Trans
              i18nKey={variant === SchoolType.SCHOOL ? "schoolList.title" : "companyList.title"}
              t={t}
            >
              Schools (<span data-cy="school-count">{{ schoolCount: schools.length }}</span>)
            </Trans>
          </StyledHeader>
        </StyledTableHeader>
        <StyledSortableTable headerCells={headerCells} rows={rowData} />
        <ConfirmSignpostSchoolDeletionModal onSchoolDeleted={onSchoolDeleted} />
      </StyledTableContainer>
    );
  },
);
